// The frame of the pages outside Me (sign-in, waitlist, passkey, consent): no
// card, one column on the ground. The mark and the word at the top left, where
// they also sit inside the app; the title is set by size and space, not bold.
// The one touch of the brand is the mark drawn huge and nearly melted into the
// ground at the right edge, only where it cannot meet the column (1280px+).
import { BrandMark, useIsDark } from "@trameria/ui-core";
import { type ReactNode, useId } from "react";

export function SignedOutPage({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  const dark = useIsDark();
  return (
    <main className="relative flex min-h-dvh flex-col overflow-hidden bg-bg px-5 text-fg sm:px-8">
      <BrandStone dark={dark} />
      <a
        href="/me"
        className="relative flex h-16 flex-none items-center gap-2.5 self-start rounded-[5px] text-body no-underline outline-offset-4"
      >
        <BrandMark size={22} />
        <span className="text-[14px]">trameria</span>
      </a>
      <div className="relative flex flex-1 items-start justify-center pb-16 pt-[max(24px,calc(22dvh-64px))]">
        <div className="w-full max-w-[360px]">
          <h1 className="text-[26px] font-normal leading-[1.35] tracking-[-0.01em] text-fg [text-wrap:balance]">
            {title}
          </h1>
          <div className="mt-6 text-body">{children}</div>
        </div>
      </div>
    </main>
  );
}

/**
 * The mark (circle, two faces and the bands) centered just outside the right
 * edge, so its left half shows. Only the accent's alpha changes per layer; the
 * bands are lighter than the faces in light and darker in dark, as in the mark.
 */
function BrandStone({ dark }: { dark: boolean }) {
  const id = useId();
  const clip = `${id}-clip`;
  const [left, center, right, band] = dark
    ? [0.03, 0.05, 0.07, 0.13]
    : [0.06, 0.045, 0.03, 0.008];
  const layer = (cx: number, cy: number, r: number, alpha: number) => (
    <g key={`${cx}-${cy}-${r}`}>
      <circle cx={cx} cy={cy} r={r} style={{ fill: "var(--color-bg)" }} />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="currentColor"
        style={{ fillOpacity: alpha }}
      />
    </g>
  );
  return (
    <svg
      aria-hidden="true"
      viewBox="2 2 28 28"
      className="pointer-events-none absolute top-1/2 right-[calc(-1.05*min(62dvh,36vw))] hidden size-[min(124dvh,72vw)] -translate-y-1/2 text-accent xl:block"
    >
      <defs>
        <clipPath id={clip}>
          <circle cx="16" cy="16" r="14" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        {layer(16, 16, 14, left)}
        {layer(38, 19, 28.3591, band)}
        {layer(44, 16, 31.8, center)}
        {layer(38, 16, 18.2, band)}
        {layer(44, 16, 21.8, right)}
      </g>
    </svg>
  );
}

/** A line under a form for a reason (red) or a notice (soft). Its height is
 * kept when empty, so nothing below moves when one appears. */
export function MessageSlot({
  error,
  notice,
}: {
  error: string | null;
  notice?: string | null;
}) {
  return (
    <p
      role={error ? "alert" : undefined}
      className={`min-h-5 text-[13px] leading-5 ${error ? "text-danger" : "text-muted"}`}
    >
      {error ?? notice}
    </p>
  );
}

/** The small text-like buttons under a form ("Use another email", "Resend code"). */
export const TEXT_BUTTON =
  "cursor-pointer text-[12px] text-muted underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline";
